import React from 'react';
import { SentimentTone } from '../GovernanceAnalyticsAiAnswers.utils';
import { TONE_BAND_METRIC } from './bandVisuals.utils';
import { SentimentThumb } from './SentimentThumb';

/**
 * One entity's two headline measures — share of voice, then sentiment — rendered identically
 * wherever a mention appears.
 *
 * The list and the detail page each used to lay these out for themselves, and drifted: the list
 * put the thumb before the name while the detail put it after the percentage, so the same mention
 * read differently on two screens. Both now render this.
 *
 * Order is share first, sentiment second: "how much of the answer was about this brand" is the
 * bigger fact, and the sentiment qualifies it.
 */
export function EntityShareAndSentiment({
  sovPercent,
  showShare,
  tone,
  percentClassName,
  thumbClassName,
}: {
  sovPercent: number;
  /**
   * Whether the share is a measurement in this context at all.
   *
   * False where the prompt's own shape fixed it — a question naming only the customer's brand hands
   * it nearly the whole answer — so the percentage is absent rather than presented as a finding.
   *
   * Required, and with no default on purpose. This component is what makes the two mention surfaces
   * agree, and a `showShare = true` default is precisely how the list was left printing a percentage
   * that the detail page for the same run refused to. A new call site has to state its answer rather
   * than inherit "show it".
   */
  showShare: boolean;
  /**
   * The mention's band on the tone measure the switch selects, or undefined when that measure was
   * never taken for it.
   */
  tone?: SentimentTone;
  percentClassName?: string;
  thumbClassName?: string;
}) {
  return (
    <>
      {showShare ? <span className={percentClassName}>{sovPercent}%</span> : null}
      {/* No measurement means no thumb at all. A neutral thumb would assert a reading nobody took,
          which is the same class of error as a fabricated score. */}
      {tone ? <SentimentThumb tone={tone} metric={TONE_BAND_METRIC} className={thumbClassName} /> : null}
    </>
  );
}
